/**
 * Connectivity honesty (D8, C4, §10 K8). Online-only: detect loss, say so once and unmistakably, disable writes,
 * never drop one silently, recover on reconnect. Two different conditions, two different sentences: the device
 * is offline, or the device is online but the server isn't answering — the second offers a retry. There is no
 * write queue — by owner decision.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface Conn { online: boolean; reachable: boolean; since: number | null; reconnectedAt: number | null; retry: () => Promise<boolean> }
const Ctx = createContext<Conn>({ online: true, reachable: true, since: null, reconnectedAt: null, retry: async () => true });
const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** Both halves of the backend: this app's server and the database's auth service. */
async function backendAnswers(): Promise<boolean> {
  try {
    const [app, db] = await Promise.all([
      fetch('/api/health', { cache: 'no-store' }),
      url ? fetch(`${url}/auth/v1/health`, { cache: 'no-store', headers: { apikey: anonKey ?? '' } }) : Promise.resolve(new Response(null, { status: 200 })),
    ]);
    return app.ok && db.ok;
  } catch {
    return false;
  }
}

export function ConnectivityProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [reachable, setReachable] = useState(true);
  const [since, setSince] = useState<number | null>(null);
  const [reconnectedAt, setReconnectedAt] = useState<number | null>(null);
  const check = useCallback(async () => {
    if (!navigator.onLine) return false;
    const ok = await backendAnswers();
    setReachable((was) => {
      if (was && !ok) setSince(Date.now());
      if (!was && ok) { setSince(null); setReconnectedAt(Date.now()); }
      return ok;
    });
    return ok;
  }, []);
  useEffect(() => {
    const down = () => { setOnline(false); setSince(Date.now()); setReconnectedAt(null); };
    const up = () => { setOnline(true); setSince(null); setReconnectedAt(Date.now()); void check(); };
    window.addEventListener('offline', down);
    window.addEventListener('online', up);
    // a heartbeat catches "connected to wifi, no internet" and a server that has stopped answering
    const beat = window.setInterval(() => void check(), 20000);
    return () => {
      window.removeEventListener('offline', down);
      window.removeEventListener('online', up);
      window.clearInterval(beat);
    };
  }, [check]);
  const value = useMemo<Conn>(() => ({ online, reachable, since, reconnectedAt, retry: check }), [online, reachable, since, reconnectedAt, check]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useConnectivity(): Conn {
  return useContext(Ctx);
}
